import { localizationService, type TranslationKey } from '../services/localization';

/**
 * Hook for using localization in React components
 * Provides easy access to translation and formatting functions
 */
export const useLocalization = () => {
  const t = (key: TranslationKey): string => localizationService.t(key);
  
  const formatDate = (date: Date | string, format?: 'short' | 'medium' | 'long') => 
    localizationService.formatDate(date, format);
  
  const formatTime = (date: Date | string, format?: '12h' | '24h') => 
    localizationService.formatTime(date, format);
  
  const formatDateTime = (date: Date | string, format?: 'short' | 'medium' | 'long') => 
    localizationService.formatDateTime(date, format);
  
  const formatNumber = (number: number, options?: Intl.NumberFormatOptions) => 
    localizationService.formatNumber(number, options);
  
  const formatWeight = (weight: number) => 
    localizationService.formatWeight(weight);
  
  const formatDuration = (minutes: number) => 
    localizationService.formatDuration(minutes);
  
  const formatVolume = (volume: number) => 
    localizationService.formatVolume(volume);
  
  const formatRelativeTime = (date: Date | string) => 
    localizationService.formatRelativeTime(date);
  
  const formatWorkoutCount = (count: number) => 
    localizationService.formatWorkoutCount(count);
  
  const formatExerciseCount = (count: number) => 
    localizationService.formatExerciseCount(count);
  
  const formatSetCount = (count: number) => 
    localizationService.formatSetCount(count);
  
  const validateCyrillicText = (text: string) => 
    localizationService.validateCyrillicText(text);
  
  const normalizeCyrillicText = (text: string) => 
    localizationService.normalizeCyrillicText(text);

  return {
    t,
    formatDate,
    formatTime,
    formatDateTime,
    formatNumber,
    formatWeight,
    formatDuration,
    formatVolume,
    formatRelativeTime,
    formatWorkoutCount,
    formatExerciseCount,
    formatSetCount,
    validateCyrillicText,
    normalizeCyrillicText,
    locale: localizationService.getLocale(),
    setLocale: localizationService.setLocale.bind(localizationService)
  };
};

export default useLocalization;